import {
  CatalogContentFile,
  CatalogContentGroup,
  CatalogContentRow,
  isCatalogContentFile,
  isCatalogContentGroup,
} from '@certhon/models';

/**
 * Tree node = the raw view row plus its resolved children. Discriminated by
 * `kind` (inherited from the row). Files are leaves; groups carry children.
 */
export type DocsNode = DocsGroupNode | DocsFileNode;

export interface DocsGroupNode extends CatalogContentGroup {
  children: DocsNode[];
  /** max(date_in) across descendants, or null. */
  last_date: Date | null;
}

export interface DocsFileNode extends CatalogContentFile {
  children?: never;
}

// WeakMap-keyed memo so multiple recursive panels reading the same query
// cache entry don't each walk the catalog. React Query keeps the rows
// array reference stable across renders until the data changes.
const treeCache = new WeakMap<CatalogContentRow[], DocsGroupNode[]>();

/**
 * Build the catalog tree from the flat api.catalog_content rows. Groups with
 * parent_group_id === null are roots.
 */
export function buildDocsTree(rows: CatalogContentRow[]): DocsGroupNode[] {
  const cached = treeCache.get(rows);
  if (cached) return cached;
  const built = buildDocsTreeUncached(rows);
  treeCache.set(rows, built);
  return built;
}

function buildDocsTreeUncached(rows: CatalogContentRow[]): DocsGroupNode[] {
  const groupNodes = new Map<number, DocsGroupNode>();
  for (const r of rows) {
    if (isCatalogContentGroup(r)) {
      groupNodes.set(r.recnum, { ...r, children: [], last_date: null });
    }
  }

  // Attach child groups to parents.
  for (const node of groupNodes.values()) {
    if (node.parent_group_id == null) continue;
    const parent = groupNodes.get(node.parent_group_id);
    if (parent) parent.children.push(node);
  }

  // Attach files to their parent group.
  for (const r of rows) {
    if (!isCatalogContentFile(r)) continue;
    const parent =
      r.parent_group_id != null ? groupNodes.get(r.parent_group_id) : null;
    if (!parent) continue;
    parent.children.push(r as DocsFileNode);
  }

  // Sort: groups before files; tie-break by ordering then duplicatenr/recnum.
  for (const node of groupNodes.values()) {
    node.children.sort(compareNodes);
  }

  // last_date = max(date_in) across the subtree.
  function fillLastDate(node: DocsGroupNode): Date | null {
    let max: Date | null = null;
    for (const child of node.children) {
      const candidate =
        child.kind === 'file' ? child.date_in : fillLastDate(child);
      if (candidate && (!max || candidate > max)) max = candidate;
    }
    node.last_date = max;
    return max;
  }

  const roots: DocsGroupNode[] = [];
  for (const node of groupNodes.values()) {
    if (node.parent_group_id == null) roots.push(node);
  }
  for (const root of roots) fillLastDate(root);
  roots.sort(compareNodes);
  return roots;
}

/** Subtree at the given group, or root list when groupId is null. */
export function getSubtree(
  rows: CatalogContentRow[],
  groupId: number | null,
): DocsNode[] {
  const tree = buildDocsTree(rows);
  if (groupId === null) return tree;
  const visit = (nodes: DocsNode[]): DocsNode[] | null => {
    for (const node of nodes) {
      if (node.kind !== 'group') continue;
      if (node.recnum === groupId) return node.children;
      const found = visit(node.children);
      if (found) return found;
    }
    return null;
  };
  return visit(tree) ?? [];
}

/** Lookup a group row by recnum. */
export function findGroup(
  rows: CatalogContentRow[],
  groupId: number,
): CatalogContentGroup | null {
  const r = rows.find((x) => x.kind === 'group' && x.recnum === groupId);
  return r ? (r as CatalogContentGroup) : null;
}

/** Lookup a file row by recnum (filetoken recnum). */
export function findFile(
  rows: CatalogContentRow[],
  fileId: number,
): CatalogContentFile | null {
  const r = rows.find((x) => x.kind === 'file' && x.recnum === fileId);
  return r ? (r as CatalogContentFile) : null;
}

type LabelLang = 'nl' | 'en' | 'de' | 'fr' | 'es';
type LabelNode = Pick<CatalogContentRow, 'recnum' | LabelLang>;

/**
 * Display label, preferring `en`, falling back to other available languages,
 * finally `#<recnum>`.
 *
 * File slots mirror the legacy catalog naming: a `prefix` (drawing number)
 * is prepended; without a prefix, duplicate slots (`duplicatenr` > 0) get a
 * 1-based suffix so same-named files stay distinguishable.
 */
export function getNodeLabel(
  node: LabelNode & Partial<Pick<CatalogContentRow, 'prefix' | 'duplicatenr'>>,
  preferred: LabelLang = 'en',
): string {
  const name = getBaseLabel(node, preferred);
  const prefix = node.prefix?.trim();
  if (prefix) return `${prefix} ${name}`;
  if (node.duplicatenr) return `${name} ${node.duplicatenr + 1}`;
  return name;
}

function getBaseLabel(node: LabelNode, preferred: LabelLang): string {
  const ordered: Array<'nl' | 'en' | 'de' | 'fr' | 'es'> = [
    preferred,
    'en',
    'nl',
    'de',
    'fr',
    'es',
  ];
  for (const k of ordered) {
    const v = node[k];
    if (typeof v === 'string') {
      const trimmed = v.trim();
      if (trimmed) return trimmed;
    }
  }
  return `#${node.recnum}`;
}

/** Revision number → display label ('first', 'rev A', 'rev B', …). */
export function revisionLabel(n: number): string {
  if (n <= 0) return 'first';
  const ch = String.fromCharCode('A'.charCodeAt(0) + (n - 1));
  return `rev ${ch}`;
}

function compareNodes(a: DocsNode, b: DocsNode): number {
  if (a.kind !== b.kind) return a.kind === 'group' ? -1 : 1;
  if (a.ordering !== b.ordering) return a.ordering - b.ordering;
  if (a.kind === 'file' && b.kind === 'file') {
    return (a.duplicatenr ?? 0) - (b.duplicatenr ?? 0);
  }
  return a.recnum - b.recnum;
}
